// Modal canónico del sistema de diseño — ÚNICA fuente para diálogos.
//
// Por qué existe: un `position: fixed` deja de ser relativo al VIEWPORT si un
// ancestro tiene `transform` / `filter` / `backdrop-filter` (pasa con layouts
// animados o con blur) → el diálogo se descuadra y "se va hasta abajo". Este
// componente lo renderiza por **Portal a document.body**, así SIEMPRE queda
// centrado en la pantalla completa, con tamaño acotado a la pantalla y scroll
// interno (nunca se desborda fuera de vista).
//
// Regla de diseño: NO reimplementar overlays a mano por página ni sobreescribir
// el centrado/tamaño con CSS local. Usar SIEMPRE <Modal>. Es theme-aware por
// tokens semánticos (bg-card/border-border/text-foreground) → claro y oscuro.

import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

type Size = 'sm' | 'md' | 'lg' | 'xl';
const MAXW: Record<Size, string> = {
  sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl',
};

export function Modal({ open, onClose, title, children, footer, size = 'lg', inShell = false }: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: Size;
  /** Opt-in: portal al marco `.orion-shell` (si existe) para heredar SU tema
   *  (variables de color por org, botón de marca). Sigue siendo `fixed` a pantalla
   *  completa: el marco no tiene transform. Sin shell → document.body como siempre. */
  inShell?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden'; // bloquea scroll de fondo
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`w-full ${MAXW[size]} max-h-[85vh] flex flex-col rounded-2xl border border-border bg-card shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        {title != null && (
          <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-border shrink-0">
            <div className="text-sm font-semibold text-foreground min-w-0">{title}</div>
            <button onClick={onClose} aria-label="Cerrar"
              className="shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-muted">
              <X size={18} />
            </button>
          </div>
        )}
        <div className="overflow-y-auto px-5 py-4 grow">{children}</div>
        {footer != null && (
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border shrink-0">{footer}</div>
        )}
      </div>
    </div>,
    (inShell && document.querySelector('.orion-shell')) || document.body,
  );
}
